import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Avatar, Card, CardContent, CardHeader, Checkbox, Radio, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { getAvatarLetters } from '../../services/helpers'
import CreateVisitPetDetailDayDropdown from './CreateVisitPetDetailDayDropdown'
import moment from 'moment'
import { warnings } from '../../utils/colors'
import PetAlert from '../Common/PetAlert'
import { getDogIssues } from '../../services/helpers'

const useStyles = makeStyles((theme) => ({
	cardHeader: {
		'& .MuiCardHeader-title': {
			color: theme.palette.secondary.main
		}
	},
	petTitle: {
		display: 'flex',
		flex: 1,
		gap: '1rem',
		'& h5': {
			fontSize: '16px',
			lineHeight: '22px',
			display: 'flex',
			alignItems: 'center'
		}
	},
	warningContainer: {
		flex: 1
	},
	warning: {
		width: 'auto',
		backgroundColor: warnings[10],
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: '700',
		textTransform: 'none',
		color: warnings[500],
		padding: '0.375rem',
		borderRadius: '0.375rem'
	},
	bigRadio: {
		'& .MuiSvgIcon-root': {
			color: '#6B4AD6',
			fill: '#6B4AD6'
		}
	}
}))

const CreateVisitPetDetail = ({ visit, setVisit, pet: propsPet }) => {
	const classes = useStyles()


	const [pet, setPet] = useState(null)

	const isGrooming = useMemo(() => {
		if (!visit) {
			return false
		}

		return visit.type === 'grooming'
	}, [visit])

	useEffect(() => {
		if (propsPet) {
			setPet(propsPet)
		}
	}, [propsPet])

	const petAlerts = useMemo(() => {
		// Only render for grooming appointments at this time.
		if (!pet || !isGrooming) {
			return null
		}

		const { permanent_service_restrictions = [], temporary_service_restrictions = [] } = pet

		const issues = getDogIssues(pet)

		if (permanent_service_restrictions.includes('Grooming')) {
			issues.push({
				type: 'danger',
				message: 'Permanent grooming ban'
			})
		}

		if (temporary_service_restrictions.includes('Grooming')) {
			issues.push({
				type: 'danger',
				message: 'Temporary grooming ban'
			})
		}

		return <PetAlert issues={issues} />
	}, [isGrooming, pet])

	const renderPetTitle = useCallback(() => {
		if (!pet || !visit) {
			return null
		}
		const { name, no_grooming_profile } = pet

		return (
			<div className={classes.petTitle}>
				<Typography variant="h5">{name}</Typography>
				{petAlerts}
				{isGrooming && no_grooming_profile ? (
					<div className={classes.warningContainer}>
						<span className={classes.warning}>Grooming profile not setup</span>
					</div>
				) : null}
			</div>
		)
	}, [pet, visit, isGrooming, petAlerts])

	const selectPet = () => {
		let pets = visit.pets
		let petIndex = pets.findIndex((p) => p._id === pet._id)

		pets[petIndex].selected = !pets[petIndex].selected

		setVisit({
			...visit,
			pets: [...pets]
		})
	}

	const selectPetGrooming = () => {
		let pets = visit.pets.map((p) => {
			if (p._id === pet._id)
				return {
					...p,
					selected: !p.selected
				}

			return {
				...p,
				selected: false
			}
		})

		setVisit({
			...visit,
			pets: [...pets]
		})
	}

	const renderPetDetail = () => (
		<Card>
			<CardHeader
				className={classes.cardHeader}
				avatar={
					<div style={{ display: 'flex', alignItems: 'center' }} className={classes.bigRadio}>
						{isGrooming ? (
							<Radio
								checked={pet.selected}
								onChange={() => selectPetGrooming()}
								classes={{ root: classes.radio, checked: classes.checked }}
							/>
						) : (
							<Checkbox value="select" checked={pet.selected} onChange={() => selectPet()} />
						)}
						<Avatar
							alt={pet.name}
							src={
								pet.profile_image_file_name
									? `https://waggle-keys-prod.s3.amazonaws.com/${pet.profile_image_file_name}`
									: null
							}>
							{getAvatarLetters(pet.name)}
						</Avatar>
					</div>
				}
				title={renderPetTitle()}
			/>
			<CardContent>
				<div>
					{visit.dates.length
						? visit.dates.map((date) => (
							<CreateVisitPetDetailDayDropdown
								key={date}
								date={moment(date).format('MMM D, YYYY')}
								visit={visit}
								pet={pet}
							/>
						))
						: 'No dates to display.'}
				</div>
			</CardContent>
		</Card>
	)

	return <div>{pet ? renderPetDetail() : null}</div>
}

export default CreateVisitPetDetail
